'use client';

import { AnimatePresence, motion } from 'framer-motion';
import FAQCardData from '../textContent/faq/faq.json';
import Link from 'next/link';
import { useState } from 'react';

const ALL = 'All';
const categories = [ALL, ...Object.keys(FAQCardData)];

/* ---------------- FAQ ITEM ---------------- */

const FaqItem = ({ item, number, isOpen, onToggle }) => (
    <div
        className={`rounded-2xl border transition-all duration-300 ${
            isOpen
                ? 'border-blue-500 bg-blue-50/60 shadow-md'
                : 'border-gray-100 bg-white shadow-sm hover:border-blue-200 hover:shadow-md'
        }`}
    >
        <button
            type="button"
            onClick={onToggle}
            aria-expanded={isOpen}
            className="w-full flex items-center gap-4 px-5 md:px-6 py-5 text-left"
        >
            <span
                className={`hidden sm:flex shrink-0 w-9 h-9 rounded-lg items-center justify-center text-sm font-bold transition-colors duration-300 ${
                    isOpen
                        ? 'bg-blue-500 text-white'
                        : 'bg-blue-50 text-blue-500'
                }`}
            >
                {String(number).padStart(2, '0')}
            </span>
            <span className="flex-1 font-bold text-gray-800 text-base md:text-lg">
                {item.title}
            </span>
            <motion.span
                animate={{ rotate: isOpen ? 45 : 0 }}
                transition={{ duration: 0.25 }}
                className={`shrink-0 w-8 h-8 rounded-full flex items-center justify-center transition-colors duration-300 ${
                    isOpen
                        ? 'bg-blue-500 text-white'
                        : 'bg-gray-100 text-gray-600'
                }`}
            >
                <i className="ri-add-line text-lg"></i>
            </motion.span>
        </button>

        <AnimatePresence initial={false}>
            {isOpen && (
                <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3, ease: 'easeOut' }}
                    className="overflow-hidden"
                >
                    <p className="px-5 md:px-6 sm:pl-[4.75rem] md:pl-[5.25rem] pb-6 text-gray-600 text-sm md:text-base leading-relaxed">
                        {item.body}
                    </p>
                </motion.div>
            )}
        </AnimatePresence>
    </div>
);

/* ---------------- MAIN ---------------- */

function AccordionAlwaysOpen() {
    const [active, setActive] = useState(ALL);
    const [openId, setOpenId] = useState(`${categories[1]}-0`);

    const groups = (active === ALL ? categories.slice(1) : [active]).map(
        (key) => ({ key, items: FAQCardData[key] }),
    );

    let counter = 0;

    return (
        <section className="w-full py-6 md:py-8 px-4">
            <div className="max-w-3xl mx-auto">
                {/* ---------------- Heading ---------------- */}
                <motion.div
                    className="text-center mb-8 md:mb-10"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.3 }}
                    transition={{ duration: 0.6, ease: 'easeOut' }}
                >
                    <p className="text-blue-500 font-bold text-sm mb-3">FAQs</p>
                    <h2 className="text-2xl md:text-4xl font-bold text-gray-800 leading-tight">
                        Frequently Asked Questions
                    </h2>
                    <p className="mt-4 text-gray-600 text-base md:text-lg">
                        Find answers to common questions about our services
                    </p>
                </motion.div>

                {/* ---------------- Category Tabs ---------------- */}
                <div
                    role="tablist"
                    aria-label="FAQ categories"
                    className="flex flex-wrap justify-center gap-2 mb-8 md:mb-10"
                >
                    {categories.map((cat) => (
                        <button
                            key={cat}
                            type="button"
                            role="tab"
                            aria-selected={active === cat}
                            onClick={() => setActive(cat)}
                            className={`text-sm font-semibold px-4 py-2 rounded-full border transition-colors duration-300 ${
                                active === cat
                                    ? 'bg-blue-500 border-blue-500 text-white shadow-md'
                                    : 'bg-white border-gray-200 text-gray-600 hover:border-blue-400 hover:text-blue-500'
                            }`}
                        >
                            {cat}
                        </button>
                    ))}
                </div>

                {/* ---------------- FAQ Groups ---------------- */}
                <AnimatePresence mode="wait">
                    <motion.div
                        key={active}
                        initial={{ opacity: 0, y: 15 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.3 }}
                        className="space-y-10"
                    >
                        {groups.map(({ key, items }) => (
                            <div key={key}>
                                {active === ALL && (
                                    <div className="flex items-center gap-3 mb-4">
                                        <h3 className="text-sm font-bold uppercase tracking-wide text-gray-500">
                                            {key}
                                        </h3>
                                        <span className="flex-1 h-px bg-gray-200" />
                                    </div>
                                )}
                                <div className="space-y-3">
                                    {items.map((item, index) => {
                                        const id = `${key}-${index}`;
                                        counter += 1;
                                        return (
                                            <FaqItem
                                                key={id}
                                                item={item}
                                                number={counter}
                                                isOpen={openId === id}
                                                onToggle={() =>
                                                    setOpenId(
                                                        openId === id
                                                            ? null
                                                            : id,
                                                    )
                                                }
                                            />
                                        );
                                    })}
                                </div>
                            </div>
                        ))}
                    </motion.div>
                </AnimatePresence>

                {/* ---------------- CTA ---------------- */}
                <div className="mt-12 text-center bg-gray-50 rounded-3xl p-8">
                    <h3 className="text-xl md:text-2xl font-bold text-gray-800">
                        Still have questions?
                    </h3>
                    <p className="mt-2 text-gray-600">
                        Our team is happy to help you find the right answer.
                    </p>
                    <Link href="/contact-sales" className="inline-block mt-5">
                        <button className="hello_button py-3 px-8 bg-blue-500 rounded-md shadow-xl hover:shadow-none transition duration-300">
                            <span className="font-bold text-white text-sm md:text-base">
                                Talk to Us
                            </span>
                        </button>
                    </Link>
                </div>
            </div>
        </section>
    );
}

export default AccordionAlwaysOpen;
